import {
	confluenceDcSpaceFilterOptionsQuery,
	confluenceSpaceKeysToIdsQuery,
	confluenceSpacesQuery,
	type EntityATI,
	searchEntitiesQuery,
	smartsRecommendedContainerQuery,
	spaceARIEntitiesQuery,
} from '@atlassian/search-client';
import { cleanExcerpt } from '@atlassian/search-common';
import { getBackendExperiment } from '@atlassian/search-experiment';

import { DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI } from '../../../../../common/constants/entities';
import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import {
	ProductKeys,
	type ProductKeys1P,
	type ProductKeys3P,
} from '../../../../../common/constants/products';
import { getProducts3PFromConfigForFilter, notEmpty } from '../../../../../common/utils/filters';
import { isHello } from '../../../../../common/utils/is-hello';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

type Space = {
	/** Space ARI */
	ari: string;
	/** Space key, e.g. "MICROS" */
	key: string;
	name: string | null;
	avatarUrl: string;
};

const productKeys1P: ProductKeys1P[] = [ProductKeys.Confluence];

const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('space');

const getProducts3PFromConfig = () => products3PFromConfig;

const LOOKUP_ENTITIES: EntityATI[] = ['ati:cloud:confluence:space'];

async function searchDcSpaces(query: string, config: CloudConfig) {
	let spacesResponse = await confluenceDcSpaceFilterOptionsQuery({
		variables: {
			query,
			cloudIdARI: `ari:cloud:confluence::site/${config.cloudId}`,
			limit: 5,
			entities: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
			product: ProductKeys.ConfluenceDC,
		},
		aggAbsoluteUrl: config.aggAbsoluteUrl,
	});

	return (
		spacesResponse?.data?.search?.search?.edges?.map(({ node }) => ({
			trackingKey: node.entity.id,
			value: node.entity.id,
			queryParamValue: node.entity.id,
			avatarUrl: node.entity?.icon?.url,
			label: cleanExcerpt(node.entity.displayName),
			product: config.product,
		})) ?? []
	);
}

export const filter: Readonly<{
    id: "space";
    type: "single-select" | "multi-select";
    queryParams: {
        key: string;
    };
    products: ProductKeys[];
    universal: boolean;
    customValueFields?: string[];
    withState: (state: unknown, config: CloudConfig) => import("../../../../../common/constants/filters/select-filter/types").SelectFilterMonad<"space">;
    getInitialState: (config: CloudConfig) => import("../../../../../common/constants/filters/select-filter/types").SelectFilterMonad<"space">;
    isCompatible: (filterId?: string) => boolean;
    isSupportCustomValue: boolean;
    isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'space',
	products: [...productKeys1P, ...getProducts3PFromConfig()],
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'spaces',
		fetcher: async (queryParams, config) => {
			if (config.product === ProductKeys.ConfluenceDC) {
				const spacesResponse = await spaceARIEntitiesQuery({
					variables: {
						ids: queryParams,
					},
				});

				const spacesOptions = spacesResponse.data?.external_entities?.space?.map((space) => ({
					trackingKey: space.id,
					value: space.id,
					label: space.displayName,
					avatarUrl: space.icon?.url || '',
					queryParamValue: space.id,
					product: config.product,
				}));

				return spacesOptions || [];
			}

			const cloudId = config.cloudId;

			// Practically cloudId should always be present, but we need to satisfy the type checker
			if (!cloudId) {
				return [];
			}
			const response = await confluenceSpaceKeysToIdsQuery({ spaceKeys: queryParams, cloudId });
			const spaceIds = response.data?.spaces?.nodes?.map((node) => node?.id).filter(notEmpty) ?? [];
			const spaceARIs = spaceIds.map((id) => `ari:cloud:confluence:${cloudId}:space/${id}`);

			const initialSpacesResponse = await confluenceSpacesQuery(spaceARIs);

			const allRecommendedSpaces: Space[] =
				initialSpacesResponse.data?.confluence.spaces.map((space) => ({
					key: space.key,
					name: space.name,
					ari: space.id,
					avatarUrl:
						space.links?.base && space.icon?.path ? `${space.links.base}${space.icon.path}` : '',
				})) || [];

			return allRecommendedSpaces.map((space) => ({
				trackingKey: space.ari,
				value: space.key,
				queryParamValue: space.key,
				avatarUrl: space.avatarUrl,
				label: space.name || '',
				product: config.product,
			}));
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			if (config.product === ProductKeys.ConfluenceDC) {
				const spaces = await searchDcSpaces('', config);
				return spaces;
			}
			const recommendedSpacesResponse = await smartsRecommendedContainerQuery({
				aggAbsoluteUrl: config.aggAbsoluteUrl,
				variables: {
					userId: config.userId || '',
					tenantId: config.cloudId || '',
				},
			});

			const allRecommendedSpaces: Space[] =
				recommendedSpacesResponse.data?.smarts.results?.flatMap(({ container }) => {
					if (!(container && container.key)) {
						return [];
					}
					return [
						{
							key: container.key,
							name: container.name,
							ari: container.id,
							avatarUrl:
								container.links?.base && container.icon?.path
									? `${container.links.base}${container.icon.path}`
									: '',
						},
					];
				}) || [];

			const recommendedSpaceOptions = allRecommendedSpaces.map((space) => {
				return {
					trackingKey: space.ari,
					value: space.key,
					queryParamValue: space.key,
					avatarUrl: space.avatarUrl,
					label: space.name || '',
					product: config.product,
				};
			});

			return recommendedSpaceOptions;
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			if (config.product === ProductKeys.ConfluenceDC) {
				return searchDcSpaces(query, config);
			}

			const backendExperiment =
				config.experimentConfig &&
				getBackendExperiment({
					experimentConfig: config.experimentConfig,
					entities: LOOKUP_ENTITIES,
					productEdition: config.productEdition,
					isHello: isHello(config.cloudId),
					productDataRegions: config.productDataRegions,
					experience: 'search-page-space-filter',
					tenantId: config.cloudId,
				});

			const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

			const response = await searchEntitiesQuery({
				aggAbsoluteUrl: config.aggAbsoluteUrl,
				variables: {
					query,
					first: 15,
					filters: {
						entities: ['ati:cloud:confluence:space'],
						locations: [`ari:cloud:confluence::site/${config.cloudId}`],
					},
					experience: 'full-page-search-space-filter',
					experimentId,
					shadowExperimentId,
					experimentLayers: experimentLayers?.map(({ name, layerId, shadowId, definitions }) => ({
						name,
						layerId,
						shadowId,
						definitions,
					})),
				},
				includeSpaceKey: true,
			});
			const { data } = response;
			if (!data) {
				return [];
			}

			return (
				data.search?.results.edges.map(({ node }) => ({
					trackingKey: node.id || '',
					value: node.key || '',
					queryParamValue: node.key ?? '',
					avatarUrl: node.iconUrl || undefined,
					label: node.title,
					product: config.product,
				})) ?? []
			);
		},
	},
	isSuggestedFilter: (primaryProduct) => primaryProduct === ProductKeys.Confluence,
});
